import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import CircularProgress from '@mui/material/CircularProgress';
import color from 'color';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
import { AVATAR_FULL_TEXTURE_SIZE } from '../constants/avatarConstants';
import { addMessageBox } from '../redux/actions/messageBoxesActions';
import { useAppDispatch } from '../redux/store';
import { getServiceAvatarUrl } from '../utils/avatarServiceUtils';
import { loadImage } from '../utils/imageUtils';
import { isTestEnvironment } from '../utils/utils';
const AVATAR_PX_SIZES = {
    small: 120,
    medium: 170,
    large: 300
};
const AVATAR_IMAGE_CSS = css `
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(6px 6px 0 rgba(0, 0, 0, 0.15));
`;
function getDataProps(isLoading, playerAvatar) {
    if (isLoading) {
        // don't add data attributes while loading is happening (it's not "real" yet)
        return {};
    }
    if (!isTestEnvironment()) {
        return {};
    }
    switch (playerAvatar.type) {
        case 'custom': {
            return Object.keys(playerAvatar.avatar.properties.parts).reduce((acc, partType) => {
                const avatarPart = playerAvatar.avatar.properties.parts[partType];
                acc[`data-avatar-${partType.toLowerCase()}-index`] = avatarPart.index;
                if (avatarPart.subType) {
                    acc[`data-avatar-${partType.toLowerCase()}-sub-type`] = avatarPart.subType;
                }
                return acc;
            }, {});
        }
        case 'unique': {
            // TODO
            return {};
        }
        case 'legacy': {
            // TODO?
            return {};
        }
    }
}
function getAriaLabel(username, size) {
    const baseLabel = username ? `${username}'s Avatar` : 'My Avatar';
    return size === 'large' ? `${baseLabel} (large)` : baseLabel;
}
export const DomAvatarDisplay = ({ username, playerAvatar, backgroundColor, isClickable, size = 'medium', trimTransparentPixels = true }) => {
    const dispatch = useAppDispatch();
    const isMounted = useMountedState();
    const [avatarImageUrl, setAvatarImageUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const latestRenderOperationId = useRef(0);
    useEffect(() => {
        async function effect() {
            setIsLoading(true);
            latestRenderOperationId.current = (latestRenderOperationId.current + 1) % 10000000;
            const currRenderOperationId = latestRenderOperationId.current;
            const newAvatarImageUrl = getServiceAvatarUrl({
                avatar: playerAvatar,
                type: 'avatar',
                size: AVATAR_FULL_TEXTURE_SIZE,
                trimTransparentPixels: true
            });
            await loadImage(newAvatarImageUrl);
            if (currRenderOperationId !== latestRenderOperationId.current) {
                // operation is old, a more recent render operation is likely in progress, so ignore these results
                return;
            }
            if (!isMounted()) {
                return;
            }
            setAvatarImageUrl(newAvatarImageUrl);
            setIsLoading(false);
        }
        effect();
    }, [isMounted, playerAvatar, trimTransparentPixels]);
    const onClick = useCallback(() => {
        dispatch(addMessageBox({ type: 'player-avatar', username, playerAvatar }));
    }, [dispatch, playerAvatar, username]);
    const containerCss = useMemo(() => {
        const sizePx = AVATAR_PX_SIZES[size];
        return css `
      padding: 2px;
      width: ${sizePx}px;
      height: ${sizePx}px;
      max-width: 100%;
      max-height: 100%;
      position: relative;
      cursor: ${isClickable ? 'pointer' : 'inherit'};
    `;
    }, [isClickable, size]);
    const loadingIndicatorContainerCss = useMemo(() => css `
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: ${color(backgroundColor).alpha(0.7).string()};
      top: 0;
      left: 0;
    `, [backgroundColor]);
    return (_jsxs("div", { "aria-label": getAriaLabel(username, size), css: containerCss, onClick: isClickable ? onClick : undefined, children: [avatarImageUrl && _jsx("img", { ...getDataProps(isLoading, playerAvatar), css: AVATAR_IMAGE_CSS, src: avatarImageUrl }), isLoading && (_jsx("div", { css: loadingIndicatorContainerCss, children: _jsx(CircularProgress, {}) }))] }));
};
